"use client"

import React, {Component} from "react";
import {observer} from "mobx-react";
import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';
import {StoreContext} from "../../stores/StoreContext";
import {AppContextType} from "../../stores/StoreLoader";
import {sidebarKey} from "../admin/sidebar/SidebarContainer";
import {IReactionDisposer, reaction, toJS} from "mobx";

@observer
class AllModals extends Component {
    public disabler: IReactionDisposer | null = null;

    static contextType = StoreContext;

    static getComponent = {
        'news': dynamic(() => import('./NewsModal/NewsModal')),
        'accordion': dynamic(() => import('./AccordionModal/AccordionItemModal')),
        'admin': dynamic(() => import('./AdminModal/AdminModal')),
        'login': dynamic(() => import('./LoginModal')),
        'gallery': dynamic(() => import("./SocialItemModal")),
        'edit-hero': dynamic(() => import("./HeroEditor/HeroEditor")),
        'file-preview': dynamic(() => import("./FilePreviewModal")),
        'collectionManagement': dynamic(() => import("./CollectionManagementModal")),
        'peopleCollectionManagement': dynamic(() => import("./PeopleCollectionManagementModal/PeopleCollectionManagementModal")),
        'schoolBlocksEvent': dynamic(() => import("./SchoolBlocksEventModal")),
        'block': dynamic(() => import("./BlockModal")),
        "schoolDistrict": dynamic(() => import("./SchoolDistrictModal"), {ssr: false}),
        "sbOrganization": dynamic(() => import("./SchoolBlocksOrganizationModal/SchoolBlocksOrganizationModal"), {ssr: false}),
        "addOrEditBlock": dynamic(() => import("./AddEditBlockModal/AddOrEditBlock")),
        "toolDrawerGrid": dynamic(() => import("./AddEditBlockModal/ToolDrawerGrid")),
        "addOrEditAlert": dynamic(() => import("./AlertEditModal/AlertEditModal")),
        "helpModal": dynamic(() => import("./HelpModal"), {ssr: false}),
        "permissions": dynamic(() => import("./PermissionsModal/PermissionsModal")),
        "calendarManagement": dynamic(() => import("./CalendarManagementModal/CalendarManagementModal")),
        "calendarList": dynamic(() => import("./CalendarManagementModal/CalendarListModal")),
        "comparePages": dynamic(() => import("./ComparePagesModal")),
        "revertPage": dynamic(() => import("./RevertDialog")),
        "fluxCapacitor": dynamic(() => import("./FluxCapacitorTimeline"), {ssr: false}),
        "googleFilePicker": dynamic(() => import("./GoogleFilePicker")),
        "schoolBlocksPerson": dynamic(() => import("./PersonModal/SchoolBlocksPersonModal")),
        "iconUpload": dynamic(() => import("./IconUpload")),
        "faviconUpload": dynamic(() => import("./IconUpload")),
        "heroImageUpload": dynamic(() => import("./HeroEditor/HeroImageUpload")),
        "statementUpload": dynamic(() => import("./StatementImageUpload/StatementImageUpload")),
        "iosAppInstallHelp": dynamic(() => import("./iosAppInstallHelp")),
        "syncGroupsModal": dynamic(() => import("./SyncGroupsModal/SyncGroupsModal")),
        "notificationChannels": dynamic(() => import("./NotificationChannels/NotificationChannels")),
        "recipientsPreview": dynamic(() => import("./NotificationRecipientsPreviewModal")),
        "brightarrow": dynamic(() => import("./BrightArrowModal")),
        "coursework": dynamic(() => import("./CourseWorkModal")),
        "blockui": dynamic(() => import("./BlockUIModal")),
        "callToActionEditor": dynamic(() => import("./CallToActionEditor/CallToActionEditorModal")),
        "organizationSelection": dynamic(() => import("./OrganizationSelectionModal")),
        "shareContentItem": dynamic(() => import("./ShareContentItemModal")),
        "autoApproveManagement": dynamic(() => import("./AutoApproveManagementModal")),
        "terraceManagementModal": dynamic(() => import("./TerraceManagement/TerraceManagementModal")),
        "iframe": dynamic(() => import("./IframeModal")),
        "cleverfeedOrgReport": dynamic(() => import("./CleverFeedOrgReportModal")),
        "spotlightTileEditor": dynamic(() => import("./SpotlightTileEditor")),
        "tileImageUpload": dynamic(() => import("./TileImageUpload")),
        "activity": dynamic(() => import("./ActivityModal/ActivityModal")),
        "AddOrEditMessagingListModal": dynamic(() => import("./Messaging/AddOrEditMessagingListModal")),
        "deliverySummary": dynamic(() => import("./AlertEditModal/NotificationSummaryModal")),
        "textInput": dynamic(()=> import("./TextInputModal")),
        "info": dynamic(()=> import("./InfoModal")),
        "contactForm": dynamic(()=> import("./ContactFormModal")),
        "locationSelection": dynamic(() => import("./LocationSelectionModal")),
        "languageSelection": dynamic(() => import("./LanguageModal")),
        "peopleSelection": dynamic(() => import("./Messaging/PeopleSelectionModal")),
    };

    static propTypes = {
        modalObj: PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]).isRequired,
            type: PropTypes.string.isRequired,
            theme: PropTypes.oneOf(['classic', 'transparent']),
        }),
    };

    state: {
        modalStack: Array<{
            type: string,
            id: string | number,
            theme?: string,
        }>
    } = {
        modalStack: []
    }

    componentDidMount() {
        const {modalStore} = this.context as AppContextType;

        this.setState({
            modalStack: toJS(modalStore.stack)
        })

        this.disabler = reaction(() => modalStore.stack.length, () => {
            if (modalStore.stack.length > this.state.modalStack.length) {
                // a modal was added, add it immediately - uses spread operator because we don't want to preserve
                // observability of modalStore.stack, but we DO want to preserve any non-serializable values of each
                // individual item
                this.setState({modalStack: [...modalStore.stack]})
            } else {
                // a modal was removed, set a timeout so we can transition out
                setTimeout(() => {
                    this.setState({modalStack: [...modalStore.stack]})
                }, 500)
            }
        })
    }

    componentWillUnmount() {
        if (this.disabler) {
            this.disabler();
        }
    }

    render() {
        const {modalStore} = this.context as AppContextType;

        return (
            <div id="allModalContainer">
                {this.state.modalStack.map((modalObj, index) => {
                    if (modalObj.type === sidebarKey) {
                        // we want the sidebar in the modalStack to consume a vertical space, but we don't want
                        // to actually render anything here
                        return <React.Fragment key={`sidebar-${index}`} />
                    }

                    const ModalComponent = AllModals.getComponent[modalObj.type];
                    return (
                        <ModalComponent
                            key={modalObj.type}
                            id={modalObj.id}
                            type={modalObj.type}
                            theme={modalObj.theme ? modalObj.theme : 'classic'}
                            index={index}
                            modalObj={modalObj}
                            active={modalStore.stack.findIndex(modal => JSON.stringify(modalObj) === JSON.stringify(modal)) !== -1}
                        />
                    );
                })}
            </div>
        );
    }
}

export default AllModals
